import { useMemo } from "react";
import { getStableLoaderPrompt } from "@/lib/prompts";

/**
 * BrandedLoader — Premium "Golden Haze" branded loading screen.
 */

const SPARKLES = [
  { top: "18%", left: "15%", size: 11, op: 0.3, delay: 0.3 },
  { top: "12%", right: "20%", size: 8, op: 0.2, delay: 0.6 },
  { top: "38%", left: "8%", size: 9, op: 0.2, delay: 0.9 },
  { bottom: "30%", right: "12%", size: 10, op: 0.25, delay: 1.2 },
  { bottom: "18%", left: "22%", size: 7, op: 0.15, delay: 1.5 },
  { top: "55%", right: "25%", size: 8, op: 0.18, delay: 1.8 },
];

interface BrandedLoaderProps {
  variant?: "fullscreen" | "inline" | "dark";
}

export function BrandedLoader({ variant = "fullscreen" }: BrandedLoaderProps) {
  const isInline = variant === "inline";
  const isDark = variant === "dark";

  // Pick one prompt per navigation and share it across every BrandedLoader
  // that mounts during the same route load (Suspense → ProtectedRoute →
  // page-level). Keyed by pathname so a new navigation rotates the prompt.
  const prompt = useMemo(() => {
    const key = typeof window !== "undefined" ? window.location.pathname : "static";
    return getStableLoaderPrompt(key);
  }, []);

  const containerClass = isInline
    ? "flex items-center justify-center py-20 relative"
    : isDark
      ? "min-h-screen flex items-center justify-center relative overflow-hidden bg-[linear-gradient(160deg,#151020_0%,#221D30_40%,#2D2742_100%)]"
      : "min-h-screen flex items-center justify-center relative overflow-hidden";

  const textColor = isDark ? "#E8C97A" : "#1A1A2E";
  const accentColor = "#E8913A";
  const sparkleColor = isDark ? "#E8C97A" : "#D4A053";

  return (
    <>
      <style>{`
        @keyframes bl-bgIn {
          from { opacity: 0; transform: scale(1.08); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes bl-bloomIn {
          from { transform: translate(-50%, -50%) scale(0); opacity: 0; }
          to { transform: translate(-50%, -50%) scale(1); opacity: 1; }
        }
        @keyframes bl-logoIn {
          from { opacity: 0; transform: scale(0.85) translateY(6px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes bl-sparkPop {
          from { opacity: 0; transform: scale(0) rotate(-20deg); }
          to { opacity: 1; transform: scale(1) rotate(0deg); }
        }
        @keyframes bl-fadeUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes bl-promptIn {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes bl-barFill {
          from { width: 0%; }
          to { width: 100%; }
        }
        @keyframes bl-fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes bl-twinkle {
          0%, 100% { opacity: 0.15; transform: scale(0.8); }
          50% { opacity: 0.45; transform: scale(1.15); }
        }
      `}</style>
      <div className={containerClass}>
        {/* Rich gradient background (Golden Haze) */}
        {!isInline && !isDark && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `
                radial-gradient(ellipse 80% 55% at 50% 42%, rgba(245,197,99,0.2), transparent 60%),
                radial-gradient(ellipse 50% 60% at 22% 18%, rgba(255,222,179,0.2), transparent),
                radial-gradient(ellipse 45% 50% at 78% 72%, rgba(232,145,58,0.08), transparent),
                linear-gradient(170deg, #FFF8EE 0%, #FFFCF7 40%, #FFF5E6 100%)
              `,
              opacity: 0,
              animation: "bl-bgIn 0.8s cubic-bezier(0.22,1,0.36,1) 0s both",
            }}
          />
        )}

        {/* Central bloom */}
        {!isInline && (
          <div
            className="absolute rounded-full pointer-events-none"
            style={{
              top: "50%",
              left: "50%",
              width: 300,
              height: 300,
              background: isDark
                ? "radial-gradient(circle, rgba(232,201,122,0.12) 0%, transparent 60%)"
                : "radial-gradient(circle, rgba(245,197,99,0.28) 0%, rgba(232,145,58,0.08) 40%, transparent 68%)",
              filter: "blur(24px)",
              WebkitFilter: "blur(24px)",
              animation: "bl-bloomIn 1s cubic-bezier(0.22,1,0.36,1) 0.05s both",
            }}
          />
        )}

        {/* Sparkles */}
        {SPARKLES.map((s, i) => (
          <span
            key={i}
            className="absolute pointer-events-none"
            style={{
              top: s.top,
              bottom: (s as Record<string, string | number>).bottom as string | undefined,
              left: s.left,
              right: (s as Record<string, string | number>).right as string | undefined,
              fontSize: s.size,
              color: sparkleColor,
              opacity: 0,
              animation: `bl-fadeIn 0.6s cubic-bezier(0.22,1,0.36,1) ${s.delay}s both, bl-twinkle 2.5s ease-in-out ${s.delay}s infinite`,
            }}
          >
            {i % 2 === 0 ? "✦" : "✧"}
          </span>
        ))}

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center">
          {/* Logo */}
          <div
            style={{
              fontFamily: "'Outfit', sans-serif",
              fontSize: 42,
              fontWeight: 800,
              color: textColor,
              opacity: 0,
              animation: "bl-logoIn 0.7s cubic-bezier(0.22,1,0.36,1) 0.2s both",
            }}
          >
            thnx
            <span
              style={{
                color: accentColor,
                fontSize: 22,
                position: "relative",
                top: -12,
                marginLeft: 2,
                display: "inline-block",
                opacity: 0,
                animation: "bl-sparkPop 0.5s cubic-bezier(0.22,1,0.36,1) 0.5s both",
              }}
            >
              ✦
            </span>
          </div>

          {/* Tagline */}
          <div
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 15,
              fontStyle: "italic",
              color: accentColor,
              marginTop: 4,
              opacity: 0,
              animation: "bl-fadeUp 0.6s cubic-bezier(0.22,1,0.36,1) 0.45s both",
            }}
          >
            daily gratitude
          </div>

          {/* Gratitude prompt */}
          {!isInline && (
            <div
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: 17,
                fontWeight: 400,
                fontStyle: "italic",
                color: textColor,
                textAlign: "center",
                lineHeight: 1.4,
                maxWidth: 260,
                marginTop: 32,
                opacity: 0,
                animation: "bl-promptIn 0.8s cubic-bezier(0.22,1,0.36,1) 0.8s both",
              }}
            >
            {prompt.before}
              <span style={{ fontStyle: "normal", color: accentColor }}>{prompt.highlight}</span>
              {prompt.after}
            </div>
          )}

          {/* Progress Bar */}
          <div
            style={{
              width: 140,
              height: 3,
              borderRadius: 2,
              background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)",
              marginTop: isInline ? 20 : 28,
              overflow: "hidden",
              opacity: 0,
              animation: `bl-fadeUp 0.4s cubic-bezier(0.22,1,0.36,1) ${isInline ? "0.5s" : "1s"} both`,
            }}
          >
            <div
              style={{
                height: "100%",
                borderRadius: 2,
                background: "linear-gradient(90deg, #F5C563, #E8913A)",
                animation: `bl-barFill 1.8s cubic-bezier(0.4,0,0.2,1) ${isInline ? "0.5s" : "1s"} both`,
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}
